import { N8nModule } from '../../types';

export const module3: N8nModule = {
  id: 'make-production',
  title: 'Production Scenarios and Optimization',
  description: 'Deploy, monitor, and optimize Make scenarios for production use.',
  content: `# Production Scenarios and Optimization

## Deploying to Production

### Pre-Deployment Checklist

1. **Testing**
   - Test with real data
   - Verify all routes
   - Check error handlers
   - Validate outputs

2. **Configuration**
   - Set appropriate scheduling
   - Configure operation limits
   - Set up notifications
   - Review permissions

3. **Documentation**
   - Add module notes
   - Document data flows
   - List dependencies
   - Create runbook

### Activation Strategies

\`\`\`
Deployment Phases:
1. Test Environment
   - Full testing with sample data
   - Verify all integrations

2. Limited Production
   - Activate with filters
   - Process subset of data
   - Monitor closely

3. Full Production
   - Remove limitations
   - Enable all features
   - Regular monitoring
\`\`\`

## Monitoring and Maintenance

### Execution History

Review and analyze scenario runs:
- **Success Rate**: Track completion percentage
- **Execution Time**: Monitor performance
- **Operation Usage**: Control costs
- **Error Patterns**: Identify issues

### Setting Up Alerts

1. **Email Notifications**
   - Scenario errors
   - Operation limits
   - Data anomalies

2. **Webhook Notifications**
   - Send to Slack/Teams
   - Trigger incidents in PagerDuty
   - Update monitoring dashboards

3. **Built-in Monitoring**
   - Organization dashboard
   - Scenario statistics
   - Operation consumption

## Performance Optimization

### Operation Reduction Techniques

1. **Efficient Filtering**
   \`\`\`
   Before Optimization (100 operations):
   Get 100 items → Process all → Filter results
   
   After Optimization (20 operations):
   Get 100 items → Filter early → Process only 20
   \`\`\`

2. **Batch Processing**
   \`\`\`
   Before: Process each item separately (100 operations)
   After: Aggregate and process in batches (10 operations)
   \`\`\`

3. **Smart Scheduling**
   - Run scenarios when needed
   - Avoid excessive polling
   - Use webhooks for instant triggers

4. **Data Store Usage**
   - Cache frequently accessed data
   - Prevent duplicate API calls
   - Store lookup tables

### Speed Optimization

1. **Parallel Processing**
   - Use routers for independent tasks
   - Run operations simultaneously
   - Reduce total execution time

2. **Minimize Module Count**
   - Combine related operations
   - Use built-in functions
   - Avoid unnecessary steps

3. **Optimize API Calls**
   - Batch requests when possible
   - Use pagination efficiently
   - Cache responses

## Scenario Templates and Reusability

### Creating Templates

1. **Generic Design**
   - Use variables for dynamic values
   - Make connections configurable
   - Document customization points

2. **Template Categories**
   - Lead management
   - Order processing
   - Report generation
   - Data synchronization

### Sharing and Collaboration

1. **Team Collaboration**
   - Share scenarios in organization
   - Set up team folders
   - Control access permissions

2. **Version Control**
   - Clone scenarios before changes
   - Document major updates
   - Keep backup copies

## Advanced Integration Patterns

### Multi-App Workflows

\`\`\`
Example: Complete Lead Processing

1. Typeform: New form submission
   ↓
2. OpenAI: Analyze and score lead
   ↓
3. Router: Based on score
   ↓
   Route A (Hot Lead):
   → Salesforce: Create high-priority lead
   → Slack: Notify sales team immediately
   → Calendly: Trigger booking link email
   
   Route B (Warm Lead):
   → HubSpot: Add to nurture sequence
   → Mailchimp: Add to newsletter
   
   Route C (Cold Lead):
   → Google Sheets: Log for future follow-up
\`\`\`

### Data Pipeline Patterns

1. **ETL Workflows**
   - Extract from multiple sources
   - Transform and clean data
   - Load into data warehouse

2. **Real-Time Sync**
   - Webhook triggers
   - Instant data updates
   - Bi-directional sync

3. **Scheduled Reports**
   - Aggregate data daily/weekly
   - Generate reports
   - Distribute to stakeholders

## Security Best Practices

### Connection Management

1. **Least Privilege**
   - Grant minimum required permissions
   - Use service accounts
   - Separate dev/prod connections

2. **Credential Rotation**
   - Regular password updates
   - API key rotation
   - OAuth token refresh

3. **Audit Trail**
   - Log all accesses
   - Monitor connection usage
   - Review permissions regularly

### Data Protection

1. **Sensitive Data Handling**
   - Encrypt stored data
   - Mask sensitive fields
   - Comply with GDPR/privacy laws

2. **Error Logs**
   - Don't log sensitive data
   - Sanitize error messages
   - Control log access

## Troubleshooting Common Issues

### Debugging Techniques

1. **Execution Inspection**
   - View detailed logs
   - Check data at each step
   - Identify failure points

2. **Incremental Testing**
   - Test modules individually
   - Verify data transformations
   - Check filters and routes

3. **Common Issues**
   - **Rate Limits**: Add delays, batch requests
   - **Timeouts**: Optimize queries, increase limits
   - **Data Mapping**: Verify field names, check types
   - **Permissions**: Review API access, update tokens

### Performance Issues

\`\`\`
Problem: Scenario runs slowly
Solutions:
- Reduce module count
- Optimize data queries
- Use parallel routes
- Enable scenario caching

Problem: High operation usage
Solutions:
- Add early filters
- Aggregate data before processing
- Use data stores for lookups
- Optimize iteration limits
\`\`\`

## Cost Management

### Operation Monitoring

1. **Track Usage**
   - Daily operation totals
   - Per-scenario breakdown
   - Historical trends

2. **Set Limits**
   - Maximum runs per interval
   - Operation caps per scenario
   - Alert thresholds

3. **Optimize Plans**
   - Right-size subscription
   - Monitor included operations
   - Plan for growth

## Best Practices Summary

1. **Design**: Plan before building, use templates
2. **Testing**: Thorough testing in all conditions
3. **Monitoring**: Set up alerts and dashboards
4. **Performance**: Optimize operations and speed
5. **Security**: Protect credentials and data
6. **Documentation**: Document complex scenarios
7. **Maintenance**: Regular reviews and updates`,
  duration: '120 minutes',
  order: 3,
  objectives: [
    'Deploy scenarios to production',
    'Monitor and maintain automations',
    'Optimize for performance and cost',
    'Implement security best practices',
    'Troubleshoot common issues'
  ],
  keyConcepts: [
    'Production deployment',
    'Monitoring and alerts',
    'Performance optimization',
    'Security practices',
    'Cost management'
  ]
};
